import { OrderLog } from "domains/order/agreggates/order/Order";
import { TransactionType } from "domains/order/agreggates/payment/transactionType";
import { IPendingPix } from "modules/payment/application/pix/pendingPixQueue";

export type PaymentFilter = "all" | "pix" | "card" | "cashless";

export interface IPaymentHistoryRow {
  id: string;
  createdAt: number;
  value: number;
  method?: TransactionType;
  methodLabel: string;
  kindLabel: string;
  name?: string;
  ref?: string;
  /** Só em PIX de comanda/recarga online: estado da fila de verificação */
  pix?: IPendingPix;
}

const METHOD_LABEL: Record<number, string> = {
  [TransactionType.credit]: "Crédito",
  [TransactionType.debit]: "Débito",
  [TransactionType.money]: "Dinheiro",
  [TransactionType.cashless]: "Cashless",
  [TransactionType.pospaid]: "Pós-pago",
  [TransactionType.misto]: "Misto",
  [TransactionType.voucher]: "Voucher",
  [TransactionType.pix]: "PIX",
  [TransactionType.wallet]: "Wallet",
};

const KIND_LABEL: Record<string, string> = {
  Payment: "Comanda",
  Recharge: "Recarga",
  WalletRecharge: "Recarga Wallet",
};

const toTimestamp = (order: OrderLog): number => {
  const fromCart = Date.parse(order.cartDate);
  return Number.isNaN(fromCart) ? 0 : fromCart;
};

const fromPix = (item: IPendingPix): IPaymentHistoryRow => ({
  id: item.order.id,
  createdAt: item.createdAt,
  value: item.value,
  method: TransactionType.pix,
  methodLabel: METHOD_LABEL[TransactionType.pix],
  kindLabel: KIND_LABEL[item.kind],
  name: item.payer?.name,
  ref: item.payer?.ref,
  pix: item,
});

/**
 * Junta os pagamentos aprovados do log de pedidos do totem com a fila de PIX.
 * PIX que ainda não virou pagamento (aguardando, expirado, falha no envio) também entra, pela fila.
 */
export const buildPaymentHistory = (
  orders: OrderLog[],
  pixItems: IPendingPix[]
): IPaymentHistoryRow[] => {
  // Um pedido pode ter mais de um PIX (cliente cancelou e gerou outro QR): todos aparecem
  const pixByOrder = new Map<string, IPendingPix[]>();
  pixItems.forEach((it) => pixByOrder.set(it.order.id, [...(pixByOrder.get(it.order.id) ?? []), it]));
  const rows: IPaymentHistoryRow[] = [];

  orders
    .filter((order) => order.paymentApproved)
    .forEach((order) => {
      const pix = pixByOrder.get(order.id);
      if (pix) {
        pixByOrder.delete(order.id);
        pix.forEach((it) => rows.push(fromPix(it)));
        return;
      }
      const method = order.payment?.paymentMethod;
      rows.push({
        id: order.id,
        createdAt: toTimestamp(order),
        value: order.totalValue,
        method,
        methodLabel: method !== undefined ? METHOD_LABEL[method] ?? "-" : "-",
        kindLabel: order.rechargeType ? KIND_LABEL[order.rechargeType] : "Pedido",
        name: order.payer?.name ?? order.customer?.name ?? undefined,
        ref: order.payer?.ref ?? order.customer?.orderPad ?? order.customer?.tag ?? undefined,
      });
    });

  pixByOrder.forEach((items) => items.forEach((it) => rows.push(fromPix(it))));

  return rows.sort((a, b) => b.createdAt - a.createdAt);
};

const CARD = [TransactionType.credit, TransactionType.debit, TransactionType.voucher];
const CASHLESS = [TransactionType.cashless, TransactionType.pospaid, TransactionType.wallet];

export const matchesPaymentFilter = (row: IPaymentHistoryRow, filter: PaymentFilter): boolean => {
  switch (filter) {
    case "pix":
      return row.method === TransactionType.pix;
    case "card":
      return row.method !== undefined && CARD.includes(row.method);
    case "cashless":
      return row.method !== undefined && CASHLESS.includes(row.method);
    default:
      return true;
  }
};
